import { useMemo } from 'react';
import { customAlphabet } from 'nanoid';
import { useAuth } from '../../../../../utils/Auth';
import { POSTER_CAROUSEL_FIELD, getCustomFieldTypes } from '../../../../../lib/customFieldTypes';
import styles from './FieldMenu.module.css';

export default function FieldMenu(props) {

    // setShowMenu is here in case we want the menu to close on selection 
    const { qrTypeParent, fields, handleScroll, setShowMenu } = props;
    const { config, dataDispatch, qr_data } = useAuth();
    const nanoid = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ', 6);
    const restrictedCategories = useMemo(() => (
        ['liquid-poster', 'for-sale'].includes(qr_data?.data?.qr_type)
            ? new Set(['displayText'])
            : null
    ), [qr_data?.data?.qr_type]);
    const customFieldTypes = useMemo(() => getCustomFieldTypes(qr_data?.data?.qr_type), [qr_data?.data?.qr_type]);
    const hasCarouselField = useMemo(() => (
        fields.some((field) => {
            const fieldConfig = config?.field_types?.fields?.[field.type] || customFieldTypes?.[field.type] || {};
            return fieldConfig?.type === 'carousel';
        })
    ), [fields, config?.field_types?.fields, customFieldTypes]);
    const displayImageOptions = useMemo(() => (
        ['liquid-poster', 'for-sale'].includes(qr_data?.data?.qr_type) && !hasCarouselField ? [POSTER_CAROUSEL_FIELD] : []
    ), [qr_data?.data?.qr_type, hasCarouselField]);

    // type options
    const {
        choices: choiceOptions,
        contact: contactChoices,
        date: dateOptions,
        file: fileOptions,
        number: numberOptions,
        rating: ratingOptions,
        signature: signatureOptions,
        text: textOptions,
        displayText: displayTextOptions,
    } = useMemo(() => {
        return Object.values(config?.field_types?.fields || {}).reduce((acc, f) => {
            if (!f.category || f?.hide) return acc;
            if (restrictedCategories && !restrictedCategories.has(f.category)) return acc;
            acc[f.category].push(f);
            return acc;
        }, {
            choices: [],
            contact: [],
            date: [],
            file: [],
            number: [],
            rating: [],
            signature: [],
            text: [],
            displayText: [],
        });
    }, [config?.field_types?.fields, restrictedCategories]);

    const handleNewId = () => {
        const newId = nanoid();

        if (fields.find(field => field.id === newId)) {
            return handleNewId();
        }
        return newId;
    };

    const handleAddField = (e) => {
        const type = e.currentTarget.id;
        const fieldConfig = config?.field_types?.fields?.[type] || customFieldTypes?.[type] || {};
        const isHeaderParagraph = fieldConfig?.type === 'headerParagraph';
        const isCarousel = fieldConfig?.type === 'carousel';

        if (isCarousel && hasCarouselField) {
            return;
        }

        const newField = {
            id: handleNewId(),
            name: isHeaderParagraph ? { header: '', paragraph: '' } : '',
            type: type,
            archived: false,
            options: [],
            required: false,
        };

        let updatedFields = [ ...fields, type === 'mU8a' ? { ...newField, scale: 5 } : newField ];

        dataDispatch({ type: qrTypeParent, data: { fields: updatedFields }})
    };

    const renderSection = (title, options, formatLabel) => {
        if (!options.length) return null;

        return (
            <div className={styles.optionsContainer}>
                <div className={styles.optionsTitle}>{title}</div>
                <div className={styles.options}>
                    {options.map((option) => (
                        <button key={option.id} className={styles.btn} id={option.id} onClick={handleAddField}>
                            <i className={iconHandler(option)} />
                            <span>{formatLabel ? formatLabel(option) : option.name}</span>
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className={styles.container} onScroll={handleScroll}>
            {renderSection('Display Text', displayTextOptions)}
            {renderSection('Display Images', displayImageOptions)}
            {renderSection('Choices', choiceOptions)}
            {renderSection('Contact Info', contactChoices)}
            {renderSection('Text', textOptions)}
            {renderSection('Rating', ratingOptions, option => option.name === 'Number Rating' ? 'Opinion Scale' : option.name)}
            {renderSection('Number', numberOptions)}
            {renderSection('File', fileOptions)}
            {renderSection('Time', dateOptions)}
            {renderSection('Signature', signatureOptions)}
        </div>
    );
};

const iconHandler = (obj) => {
    const type = obj.type;
    const name = obj.name.toLowerCase();
    switch (type) {
        case 'checkbox':
            if (name === 'checklist') {
                return 'far fa-clipboard-list-check';
            } else {
                return 'far fa-square-check';
            }
        case 'select':
            return 'far fa-list-dropdown';
        case 'radio':
            return 'far fa-circle-dot';
        case 'text':
            return 'far fa-text';
        case 'textarea':
            return 'far fa-paragraph';
        case 'paragraph':
            return 'far fa-paragraph';
        case 'headerParagraph':
            return 'far fa-paragraph';
        case 'carousel':
            return 'far fa-images';
        case 'banner':
            return 'fas fa-pennant';
        case 'divider':
            return 'fas fa-horizontal-rule';
        case 'icon-rating':
            return 'far fa-star';
        case 'num-rating':
            return 'fas fa-signal';
        case 'slide-scale':
            return 'fas fa-slider';
        case 'number':
            return 'far fa-abacus';
        case 'datetime-local':
            return 'far fa-calendar-clock';
        case 'signature':
            return 'far fa-signature';
        case 'header':
            return 'far fa-heading';
        case 'file':
            return 'far fa-file-arrow-up';
        case 'email':
            return 'far fa-envelope';
        case 'phone':
            return 'fas fa-phone';
        case 'address':
            return 'far fa-location-dot';
        default:
            return 'fa-kit fa-liquidqr';
    }
};
